import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { Suspense, lazy, useEffect, useState } from 'react';
import api from './lib/api';
import { useAuthStore } from './store/authStore';
import { RequireAuth } from './components/auth/RequireAuth';
import { AppLayout } from './components/layout/AppLayout';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/app/DashboardPage'));
const EditorPage = lazy(() => import('./pages/app/EditorPage'));
const TrashPage = lazy(() => import('./pages/app/TrashPage'));
const ShareViewPage = lazy(() => import('./pages/public/ShareViewPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,        // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-surface-0)' }}>
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--color-brand-500)', borderTopColor: 'transparent' }}
        />
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Loading…</span>
      </div>
    </div>
  );
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const { setAuth, logout } = useAuthStore();

  useEffect(() => {
    const attemptRestore = async () => {
      try {
        const { data } = await api.post('/auth/refresh');
        
        // Also fetch the latest user info
        const userRes = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${data.data.accessToken}` }
        });
        
        setAuth(userRes.data.data, data.data.accessToken);
      } catch {
        logout();
      } finally {
        setIsInitializing(false);
      }
    };

    attemptRestore();
  }, [setAuth, logout]);

  if (isInitializing) {
    return <PageLoader />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthInitializer>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/s/:slug" element={<ShareViewPage />} />

            {/* Protected app routes */}
            <Route element={<RequireAuth />}>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
                <Route path="/app/dashboard" element={<DashboardPage />} />
                <Route path="/app/trash" element={<TrashPage />} />
              </Route>
              {/* Editor usually needs full screen without sidebar */}
              <Route path="/app/editor/:id" element={<EditorPage />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthInitializer>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
